import { Suspense, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Badge } from "@/components/catalyst/badge";
import { workOrderRecordRoute } from "@/app/ProDrivers/Common/Routes/proDriverRoutes";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ActivityIcon } from "lucide-react";
import { ModelType, QuoteStatus, UpdateQuoteInput } from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { useQuoteActions } from "@/app/Quotes/hooks/useQuoteActions";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { updateQuoteMutation } from "@/app/Quotes/GraphQL/quoteMutations";
import {
  quoteRecordQuery,
  QuoteRecord,
} from "@/app/Quotes/GraphQL/quoteRecordQuery";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { QuoteDetailForm } from "@/app/Quotes/Components/Forms/QuoteDetailForm";
import { QuoteLineItemForm } from "@/app/Quotes/Components/QuoteLineItemForm";
import { QuoteLineItemTable } from "@/app/Quotes/Components/QuoteLineItemsTable";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { quoteLineItemValidation } from "@/app/Quotes/validations/quoteLineItemValidation";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { QuoteDeclinedAlert } from "@/app/Quotes/Components/QuoteDeclinedAlert";

const quoteStatusColorMap: Record<
  QuoteStatus,
  "zinc" | "yellow" | "green" | "red" | "blue"
> = {
  [QuoteStatus.Draft]: "zinc",
  [QuoteStatus.Issued]: "yellow",
  [QuoteStatus.Accepted]: "green",
  [QuoteStatus.Rejected]: "red",
  [QuoteStatus.Expired]: "zinc",
  [QuoteStatus.Voided]: "red",
  [QuoteStatus.Invoiced]: "blue",
};

type TabId = "details" | "activity";

export function WorkOrderQuoteDrawer() {
  const navigate = useNavigate();
  const search = useSearch({
    from: workOrderRecordRoute.id,
  });
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const isOpen = !!search.quoteId;

  function handleClose() {
    navigate({
      to: ".",
      search: (prev: { quoteId?: string }) => ({
        ...prev,
        quoteId: undefined,
      }),
    });
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <DrawerOverlay />
      <DrawerContent showCloseButton={true} onClose={handleClose}>
        {search.quoteId ? (
          <div className="flex h-full flex-col overflow-hidden">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <QuoteSheetContent
                quoteId={search.quoteId}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </Suspense>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

function QuoteSheetContent({
  quoteId,
  activeTab,
  setActiveTab,
}: {
  quoteId: string;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const { data } = useSuspenseGqlQuery(quoteRecordQuery, { id: quoteId });
  const quote = data.quote;

  if (!quote) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Quote not found</p>
      </div>
    );
  }

  return (
    <>
      <QuoteSheetHeader
        quote={quote}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <DrawerBody>
        <TabContent quote={quote} activeTab={activeTab} />
      </DrawerBody>
    </>
  );
}

function TabContent({
  quote,
  activeTab,
}: {
  quote: QuoteRecord;
  activeTab: TabId;
}) {
  switch (activeTab) {
    case "details":
      return <QuoteDetailsTab quote={quote} />;
    case "activity":
      return <QuoteActivityTab quote={quote} />;
    default:
      return null;
  }
}

function QuoteSheetHeader({
  quote,
  activeTab,
  setActiveTab,
}: {
  quote: QuoteRecord;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const t = useTranslations("quote");
  const getActions = useQuoteActions();

  return (
    <DrawerHeader>
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
            <DocumentTextIcon className="h-6 w-6 text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <DrawerTitle>{quote.reference}</DrawerTitle>
              <Badge color={quoteStatusColorMap[quote.status]}>
                {t(`status.${quote.status}`)}
              </Badge>
            </div>
            <DrawerDescription>
              {formatCurrency(quote.amount_inc_tax, quote.currency)}
            </DrawerDescription>
          </div>
        </div>
        <ActionGroupButtons buttons={getActions(quote)} />
      </div>
      <div className="px-6">
        <QuoteTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </DrawerHeader>
  );
}

function QuoteTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const tabs = [
    {
      id: "details" as const,
      name: "Details",
      Icon: DocumentTextIcon,
    },
    {
      id: "activity" as const,
      name: "Activity",
      Icon: ActivityIcon,
    },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`group relative flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
            activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <tab.Icon
            className={`h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
              activeTab === tab.id
                ? "text-indigo-600"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
          <span>{tab.name}</span>
        </button>
      ))}
    </div>
  );
}

function QuoteDetailsTab({ quote }: { quote: QuoteRecord }) {
  const t = useTranslations("quote");
  const { mutateAsync } = useGqlMutation(updateQuoteMutation);
  const { isAdmin } = useTenant();

  return (
    <div className="space-y-6">
      <QuoteDeclinedAlert quote={quote} />

      {/* Quote Details Card */}
      <RecordScreenFormCard<UpdateQuoteInput>
        title="Details"
        isEditable={isAdmin}
        initialValues={{
          quote_date: quote.quote_date,
          expiry_date: quote.expiry_date,
          currency: quote.currency,
          tax_inclusion_type: quote.tax_inclusion_type,
          payee: {
            connect: quote.payee.id,
          },
          additional_notations: quote.additional_notations,
          purchase_order_number: quote.purchase_order_number,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: quote.id,
            input: values,
          });
        }}
        view={
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Reference</dt>
              <dd className="font-medium text-gray-900">{quote.reference}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd>
                <Badge color={quoteStatusColorMap[quote.status]}>
                  {t(`status.${quote.status}`)}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Quote Date</dt>
              <dd className="font-medium text-gray-900">
                {formatDate(quote.quote_date)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Expiry Date</dt>
              <dd className="font-medium text-gray-900">
                {formatDate(quote.expiry_date)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Payee</dt>
              <dd className="font-medium text-gray-900">{quote.payee.name}</dd>
            </div>
            {quote.purchase_order_number ? (
              <div>
                <dt className="text-gray-500">PO Number</dt>
                <dd className="font-medium text-gray-900">
                  {quote.purchase_order_number}
                </dd>
              </div>
            ) : null}
            {quote.payee.billing_email ? (
              <div>
                <dt className="text-gray-500">Billing Email</dt>
                <dd className="font-medium text-gray-900">
                  {quote.payee.billing_email}
                </dd>
              </div>
            ) : null}
            {quote.additional_notations ? (
              <div className="col-span-2">
                <dt className="text-gray-500">Additional Notes</dt>
                <dd className="whitespace-pre-wrap font-medium text-gray-900">
                  {quote.additional_notations}
                </dd>
              </div>
            ) : null}
          </dl>
        }
      >
        <QuoteDetailForm />
      </RecordScreenFormCard>

      {/* Line Items Card */}
      <RecordScreenFormCard<UpdateQuoteInput>
        title="Line Items"
        isEditable={isAdmin}
        initialValues={{
          currency: quote.currency,
          tax_inclusion_type: quote.tax_inclusion_type,
          lineItems: {
            upsert: quote.lineItems.map((item) => ({
              id: item.id,
              unit_amount: item.unit_amount,
              description: item.description!,
              quantity: item.quantity,
              tax_type: item.tax_type,
            })),
            delete: [],
          },
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: quote.id,
            input: values,
          });
        }}
        validationSchema={toFormikValidationSchema(quoteLineItemValidation)}
        view={<QuoteLineItemTable quote={quote} />}
      >
        <QuoteLineItemForm />
      </RecordScreenFormCard>
    </div>
  );
}

function QuoteActivityTab({ quote }: { quote: QuoteRecord }) {
  return <ActivityLogFeed modelId={quote.id} modelType={ModelType.Quote} />;
}
